import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import RequireStore from "@/components/RequireStore";
import { toast } from "sonner";
import { formatCurrency, formatDate, todayISO, MONTH_NAMES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Minus, CheckCircle2 } from "lucide-react";

const now = new Date();

export default function Riconciliazioni() {
  const { storeId } = useStore();
  const [list, setList] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [sign, setSign] = useState(1);
  const [tipo, setTipo] = useState("rettifica");
  const [motivazione, setMotivazione] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/reconciliations", { params: { store_id: storeId } });
    setList(data);
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const val = (parseFloat(String(amount).replace(",", ".")) || 0) * sign;
    if (!val) return toast.error("Inserisci un importo valido");
    try {
      await api.post("/reconciliations", { store_id: storeId, date, amount: val, tipo, motivazione, note });
      toast.success("Riconciliazione registrata");
      setAmount(""); setMotivazione(""); setNote("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <RequireStore>
    <div className="space-y-6" data-testid="riconciliazioni-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Riconciliazioni</h1>
        <p className="text-sm text-muted-foreground">Registra differenze positive o negative. Ogni voce resta nello storico e non viene mai cancellata.</p>
      </div>

      <form onSubmit={submit} className="grid gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-2" data-testid="ricon-form">
        <div>
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12" data-testid="ricon-date-input" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Importo</Label>
          <div className="flex gap-2">
            <div className="flex overflow-hidden rounded-md border border-border">
              <button type="button" onClick={() => setSign(1)} className={`px-3 ${sign === 1 ? "bg-emerald-500 text-white" : "text-muted-foreground"}`} data-testid="ricon-sign-plus"><Plus className="h-4 w-4" /></button>
              <button type="button" onClick={() => setSign(-1)} className={`px-3 ${sign === -1 ? "bg-destructive text-white" : "text-muted-foreground"}`} data-testid="ricon-sign-minus"><Minus className="h-4 w-4" /></button>
            </div>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-muted-foreground">€</span>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 pl-8 tabular-nums" placeholder="0,00" data-testid="ricon-amount-input" />
            </div>
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tipo</Label>
          <Input value={tipo} onChange={(e) => setTipo(e.target.value)} className="h-12" placeholder="rettifica" data-testid="ricon-tipo-input" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Motivazione</Label>
          <Input value={motivazione} onChange={(e) => setMotivazione(e.target.value)} className="h-12" placeholder="Es. ammanco cassa" data-testid="ricon-motivo-input" />
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="ricon-note-input" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" className="h-12 w-full font-bold sm:w-auto" data-testid="ricon-submit-btn">
            <CheckCircle2 className="mr-2 h-5 w-5" /> Registra riconciliazione
          </Button>
        </div>
      </form>

      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4 font-display font-bold">Storico riconciliazioni</div>
        <div className="divide-y divide-border/50">
          {list.length ? list.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-5 py-3" data-testid={`ricon-item-${r.id}`}>
              <div>
                <div className="font-medium">{formatDate(r.date)} · <span className="text-muted-foreground">{r.tipo}</span></div>
                <div className="text-sm text-muted-foreground">{r.motivazione || "—"} {r.created_by && `· ${r.created_by}`}</div>
              </div>
              <div className={`font-display text-lg font-bold tabular-nums ${r.amount < 0 ? "text-destructive" : "text-emerald-400"}`}>
                {r.amount > 0 ? "+" : ""}{formatCurrency(r.amount)}
              </div>
            </div>
          )) : <div className="px-5 py-10 text-center text-muted-foreground">Nessuna riconciliazione registrata.</div>}
        </div>
      </div>
    </div>
    </RequireStore>
  );
}
